/**
 * Date and time formatting utilities
 * Formats dates based on user's preferred locale and timezone
 */

import { s__, n__ } from '@/locale'

/**
 * Get the browser's timezone
 * @returns {string} Browser timezone (e.g., 'Europe/Moscow')
 */
export function getBrowserTimezone() {
  if (typeof Intl !== 'undefined' && Intl.DateTimeFormat) {
    return Intl.DateTimeFormat().resolvedOptions().timeZone
  }
  return 'UTC'
}

/**
 * Get the browser's locale
 * @returns {string} Browser locale (e.g., 'ru', 'en')
 */
export function getBrowserLocale() {
  if (typeof navigator !== 'undefined' && navigator.language) {
    return navigator.language.substring(0, 2)
  }
  return 'en'
}

/**
 * Get locale string for Intl.DateTimeFormat
 * @param {string} locale - Locale code ('ru', 'en', etc.)
 * @returns {string} Full locale string for Intl
 */
function getIntlLocale(locale) {
  const localeMap = {
    ru: 'ru-RU',
    en: 'en-GB', // Use en-GB for 24h format by default
    'en-US': 'en-US',
  }
  return localeMap[locale] || locale || 'en-GB'
}

/**
 * Format a date/time string
 * @param {string|Date} dateInput - ISO date string or Date object
 * @param {Object} options - Formatting options
 * @param {string} [options.locale] - User's preferred locale ('ru', 'en', etc.)
 * @param {string} [options.timezone] - User's preferred timezone
 * @param {string} [options.format='datetime'] - Format type: 'date', 'time', 'datetime', 'dateShort', 'timeShort'
 * @returns {string} Formatted date/time string
 */
export function formatDateTime(dateInput, options = {}) {
  if (!dateInput) return ''

  try {
    const date = dateInput instanceof Date ? dateInput : new Date(dateInput)
    if (isNaN(date.getTime())) return String(dateInput)

    const locale = options.locale || getBrowserLocale()
    const timezone = options.timezone || getBrowserTimezone()
    const format = options.format || 'datetime'
    const intlLocale = getIntlLocale(locale)

    // Determine if we should use 24-hour format
    // Russian and most European locales use 24h
    const use24Hour = [
      'ru',
      'de',
      'fr',
      'es',
      'it',
      'pt',
      'nl',
      'pl',
      'uk',
      'cs',
      'sk',
      'hu',
    ].includes(locale)

    let formatOptions = { timeZone: timezone }

    switch (format) {
      case 'date':
        // Full date: 29.11.2025 (ru) or 29/11/2025 (en-GB) or 11/29/2025 (en-US)
        formatOptions = {
          ...formatOptions,
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        }
        break

      case 'dateShort':
        // Short date: 29 нояб. (ru) or 29 Nov (en)
        formatOptions = {
          ...formatOptions,
          month: 'short',
          day: 'numeric',
        }
        break

      case 'dateLong':
        // Long date: 29 ноября 2025 г. (ru) or 29 November 2025 (en)
        formatOptions = {
          ...formatOptions,
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        }
        break

      case 'time':
      case 'timeShort':
        // Time without seconds: 18:00 (24h) or 6:00 PM (12h)
        formatOptions = {
          ...formatOptions,
          hour: '2-digit',
          minute: '2-digit',
          hour12: !use24Hour,
        }
        break

      case 'timeFull':
        // Time with seconds: 18:00:00 (24h) or 6:00:00 PM (12h)
        formatOptions = {
          ...formatOptions,
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: !use24Hour,
        }
        break

      case 'datetime':
      default:
        // Full datetime: 29.11.2025, 18:00 (ru) or 29/11/2025, 6:00 PM (en)
        formatOptions = {
          ...formatOptions,
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          hour12: !use24Hour,
        }
        break

      case 'datetimeFull':
        // Full datetime with seconds: 29.11.2025, 18:00:00 (ru)
        formatOptions = {
          ...formatOptions,
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: !use24Hour,
        }
        break
    }

    return new Intl.DateTimeFormat(intlLocale, formatOptions).format(date)
  } catch (e) {
    console.warn('Date formatting error:', e)
    return String(dateInput)
  }
}

/**
 * Format a date only (no time)
 * @param {string|Date} dateInput - ISO date string or Date object
 * @param {Object} options - Formatting options
 * @returns {string} Formatted date string
 */
export function formatDate(dateInput, options = {}) {
  return formatDateTime(dateInput, { ...options, format: 'date' })
}

/**
 * Format a time only (no date)
 * @param {string|Date} dateInput - ISO date string or Date object
 * @param {Object} options - Formatting options
 * @returns {string} Formatted time string
 */
export function formatTime(dateInput, options = {}) {
  return formatDateTime(dateInput, { ...options, format: 'timeShort' })
}

/**
 * Format a relative date (e.g., "today", "tomorrow", "in 3 days")
 * @param {string|Date} dateInput - ISO date string or Date object
 * @param {Object} options - Formatting options
 * @returns {string} Relative date string or formatted date if too far
 */
export function formatRelativeDate(dateInput, options = {}) {
  if (!dateInput) return ''

  try {
    const date = dateInput instanceof Date ? dateInput : new Date(dateInput)
    if (isNaN(date.getTime())) return String(dateInput)

    const now = new Date()
    const timezone = options.timezone || getBrowserTimezone()

    // Get dates in the target timezone for comparison
    // This ensures "today" and "yesterday" are calculated based on the user's timezone
    const dateFormatter = new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    })

    const targetDateStr = dateFormatter.format(date)
    const todayStr = dateFormatter.format(now)

    const targetDate = new Date(targetDateStr)
    const today = new Date(todayStr)

    const diffMs = today - targetDate
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

    if (diffDays === 0) {
      return s__('DatetimeFormatter|Today')
    } else if (diffDays === 1) {
      return s__('DatetimeFormatter|Yesterday')
    } else if (diffDays > 0 && diffDays < 7) {
      // Use proper pluralization for "X days ago"
      // Note: Translation uses %{count}, so we pass count in variables
      return n__(
        'DatetimeFormatter|%{days} day ago',
        'DatetimeFormatter|%{days} days ago',
        diffDays,
        {
          days: diffDays,
          count: diffDays, // Translation uses %{count}
        }
      )
    }

    // Fall back to formatted date for dates further away or in the future
    return formatDateTime(dateInput, { ...options, format: 'dateShort' })
  } catch (e) {
    console.warn('Relative date formatting error:', e)
    return String(dateInput)
  }
}
